import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'antd';
import { X, Brain, MessageSquare, TrendingUp, AlertTriangle, CheckCircle, User, Bot } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { BriefRubricScores, GuardrailFlags, hasBriefScores } from '../../../features/assessments/BriefReportScores';
import SomethingWrong from '../../../features/assessments/SomethingWrong';

const InterviewDetailsModal = ({ isOpen, onClose, resume, interviewType, jobId }) => {
  const [interviewData, setInterviewData] = useState(null);
  const [report, setReport] = useState(null);   // NEW: agent-driven InterviewReport
  const [loading, setLoading] = useState(false);

  // Map the UI interviewType to the InterviewReport "round" key.
  const ROUND_KEY = {
    conversational: 'preScreening',
    preScreening: 'preScreening',
    psychoanalytical: 'psychoanalytical',
    finalInterview: 'finalInterview',
    coding: 'coding',
  };

  const fetchInterviewDetails = useCallback(async () => {
    setLoading(true);
    setReport(null);
    setInterviewData(null);
    try {
      // ── 1) Prefer the NEW agent-driven InterviewReport ──
      // The agent (audio) interviews write an InterviewReport, not the legacy
      // ConversationalInterview session — so we look here first. This is the
      // source HR trusts (transcript + axis scores + narrative).
      try {
        const round = ROUND_KEY[interviewType];
        const rep = await api.get(`/reports/resume/${resume._id}`);
        const list = rep.data?.data || [];
        const match = list.find((r) => r.round === round);
        if (match) {
          setReport(match);
          setLoading(false);
          return;   // short-circuit — don't show the stale legacy panel
        }
      } catch (e) { /* no report yet — fall back to legacy */ }

      // ── 2) Legacy fallback (old text-mode sessions) ──
      let response;
      switch (interviewType) {
        case 'conversational':
        case 'preScreening':
          response = await api.get(`/conversational-interview/details/${resume._id}`);
          break;
        case 'psychoanalytical':
          response = await api.get(`/psychoanalytical/details/${resume._id}`);
          break;
        case 'finalInterview':
          response = await api.get(`/final-interview/details/${resume._id}`);
          break;
        default:
          throw new Error('Unknown interview type');
      }
      setInterviewData(response.data.data);
    } catch (error) {
      console.error('Error fetching interview details:', error);
      toast.error('Failed to load interview details');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, interviewType]);

  useEffect(() => {
    if (isOpen && resume && interviewType) {
      fetchInterviewDetails();
    }
  }, [isOpen, resume, interviewType, fetchInterviewDetails]);

  const getInterviewTypeTitle = () => {
    switch (interviewType) {
      case 'conversational':
      case 'preScreening':
        return 'Conversational Pre-Screening Interview';
      case 'psychoanalytical':
        return 'Psychoanalytical Assessment';
      case 'finalInterview':
        return 'Final Interview';
      default:
        return 'Interview Details';
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const duration = new Date(endTime) - new Date(startTime);
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const renderConversationalInterview = () => {
    if (!interviewData) return null;

    const { session, finalAssessment } = interviewData;

    return (
      <div className="space-y-6">
        {/* Interview Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Interview Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{session?.questionCount || 0}</div>
              <div className="text-sm text-gray-600">Questions Asked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {finalAssessment?.overallScore || 0}%
              </div>
              <div className="text-sm text-gray-600">Overall Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {formatDuration(session?.startedAt, session?.finalAssessment?.completedAt)}
              </div>
              <div className="text-sm text-gray-600">Duration</div>
            </div>
            <div className="text-center">
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                finalAssessment?.recommendation === 'strongly_recommend' || finalAssessment?.recommendation === 'recommend' 
                  ? 'bg-green-100 text-green-800' 
                  : finalAssessment?.recommendation === 'neutral'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {finalAssessment?.recommendation?.replace('_', ' ').toUpperCase() || 'N/A'}
              </div>
              <div className="text-sm text-gray-600 mt-1">Recommendation</div>
            </div>
          </div>
        </div>

        {/* Detailed Scores */}
        {finalAssessment && (
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Detailed Assessment Scores
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.verificationScore || 0)}`}>
                  {finalAssessment.verificationScore || 0}%
                </div>
                <div className="text-sm text-gray-600">Verification Score</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.technicalScore || 0)}`}>
                  {finalAssessment.technicalScore || 0}%
                </div>
                <div className="text-sm text-gray-600">Technical Score</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.communicationScore || 0)}`}>
                  {finalAssessment.communicationScore || 0}%
                </div>
                <div className="text-sm text-gray-600">Communication Score</div>
              </div>
            </div>
          </div>
        )}

        {/* Conversation History */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Conversation History
          </h3>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {session?.conversation?.map((item, index) => (
              <div key={index} className="border-l-4 border-gray-200 pl-4">
                {/* Question */}
                <div className="mb-2">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="font-medium text-blue-800 mb-1">Question {index + 1}</div>
                        <div className="text-gray-800">{item.question}</div>
                        <div className="text-xs text-gray-500 mt-2 flex items-center gap-4">
                          <span>Type: {item.questionType || 'general'}</span>
                          {item.targetArea && <span>Target: {item.targetArea}</span>}
                          <span>{new Date(item.timestamp).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Answer */}
                {item.response && (
                  <div className="mb-2 ml-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="font-medium text-green-800 mb-1">Candidate Response</div>
                          <div className="text-gray-800">{item.response}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Analysis */}
                {item.aiAnalysis && (
                  <div className="ml-8 mb-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        AI Analysis
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.credibility || 0)}`}>
                            {item.aiAnalysis.credibility || 0}
                          </div>
                          <div className="text-xs text-gray-600">Credibility</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.consistency || 0)}`}>
                            {item.aiAnalysis.consistency || 0}
                          </div>
                          <div className="text-xs text-gray-600">Consistency</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.relevance || 0)}`}>
                            {item.aiAnalysis.relevance || 0}
                          </div>
                          <div className="text-xs text-gray-600">Relevance</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.verificationScore || 0)}`}>
                            {item.aiAnalysis.verificationScore || 0}
                          </div>
                          <div className="text-xs text-gray-600">Verification</div>
                        </div>
                      </div>
                      
                      {/* Red Flags */}
                      {item.aiAnalysis.redFlags && item.aiAnalysis.redFlags.length > 0 && (
                        <div className="mb-2">
                          <div className="font-medium text-red-700 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Red Flags:
                          </div>
                          <div className="space-y-1">
                            {item.aiAnalysis.redFlags.map((flag, flagIndex) => (
                              <div key={flagIndex} className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
                                • {flag}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Positive Indicators */}
                      {item.aiAnalysis.positiveIndicators && item.aiAnalysis.positiveIndicators.length > 0 && (
                        <div>
                          <div className="font-medium text-green-700 mb-1 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Positive Indicators:
                          </div>
                          <div className="space-y-1">
                            {item.aiAnalysis.positiveIndicators.map((indicator, indicatorIndex) => (
                              <div key={indicatorIndex} className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                                • {indicator}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final Assessment Details */}
        {finalAssessment && (
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">Final Assessment Details</h3>
            
            {/* Verification Results */}
            {finalAssessment.verificationResults && (
              <div className="mb-4">
                <h4 className="font-medium mb-2">Verification Results:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(finalAssessment.verificationResults).map(([key, value]) => (
                    <div key={key} className={`flex items-center gap-2 p-2 rounded text-sm ${
                      value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {value ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths */}
            {finalAssessment.strengths && finalAssessment.strengths.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium mb-2 text-green-700">Strengths:</h4>
                <div className="space-y-1">
                  {finalAssessment.strengths.map((strength, index) => (
                    <div key={index} className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                      • {strength}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Concerns */}
            {finalAssessment.concerns && finalAssessment.concerns.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium mb-2 text-red-700">Areas of Concern:</h4>
                <div className="space-y-1">
                  {finalAssessment.concerns.map((concern, index) => (
                    <div key={index} className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                      • {concern}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reasoning Summary */}
            {finalAssessment.reasoningSummary && (
              <div className="mb-4">
                <h4 className="font-medium mb-2">Reasoning Summary:</h4>
                <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                  {finalAssessment.reasoningSummary}
                </div>
              </div>
            )}

            {/* Detailed Analysis */}
            {finalAssessment.detailedAnalysis && (
              <div>
                <h4 className="font-medium mb-2">Detailed Analysis:</h4>
                <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 max-h-32 overflow-y-auto">
                  {finalAssessment.detailedAnalysis}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPsychoanalyticalInterview = () => {
    if (!interviewData) return null;

    const { questions, result, duration } = interviewData;

    return (
      <div className="space-y-6">
        {/* Interview Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5" />
            Assessment Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{questions?.length || 0}</div>
              <div className="text-sm text-gray-600">Questions Asked</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getScoreColor(result?.score || 0)}`}>
                {result?.score || 0}%
              </div>
              <div className="text-sm text-gray-600">Final Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{duration || 'N/A'}</div>
              <div className="text-sm text-gray-600">Duration</div>
            </div>
          </div>
        </div>

        {/* Questions and Answers */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Interview Conversation
          </h3>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {questions?.map((item, index) => (
              <div key={index} className="border-l-4 border-gray-200 pl-4">
                <div className="mb-2">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="font-medium text-blue-800 mb-1">Question {index + 1}</div>
                        <div className="text-gray-800">{item.question}</div>
                        <div className="text-xs text-gray-500 mt-2">
                          {new Date(item.askedAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {item.answer && (
                  <div className="ml-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="font-medium text-green-800 mb-1">Candidate Response</div>
                          <div className="text-gray-800">{item.answer}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final Feedback */}
        {result?.feedback && (
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">AI Feedback & Analysis</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-gray-700 whitespace-pre-wrap">{result?.feedback || ''}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFinalInterview = () => {
    if (!interviewData) return null;

    const { session, finalAssessment, context } = interviewData;

    return (
      <div className="space-y-6">
        {/* Interview Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5" />
            Final Interview Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{session?.questionCount || 0}</div>
              <div className="text-sm text-gray-600">Questions Asked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{finalAssessment?.overallScore || 0}%</div>
              <div className="text-sm text-gray-600">Overall Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{formatDuration(session?.startedAt, finalAssessment?.completedAt)}</div>
              <div className="text-sm text-gray-600">Duration</div>
            </div>
            <div className="text-center">
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                finalAssessment?.recommendation === 'strongly_recommend' || finalAssessment?.recommendation === 'recommend' 
                  ? 'bg-green-100 text-green-800' 
                  : finalAssessment?.recommendation === 'neutral'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {finalAssessment?.recommendation?.replace('_', ' ').toUpperCase() || 'N/A'}
              </div>
              <div className="text-sm text-gray-600 mt-1">Recommendation</div>
            </div>
          </div>
        </div>

        {/* Detailed Scores */}
        {finalAssessment && (
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Detailed Assessment Scores
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.technicalScore || 0)}`}>{finalAssessment.technicalScore || 0}%</div>
                <div className="text-sm text-gray-600">Technical</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.behavioralScore || 0)}`}>{finalAssessment.behavioralScore || 0}%</div>
                <div className="text-sm text-gray-600">Behavioral</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.communicationScore || 0)}`}>{finalAssessment.communicationScore || 0}%</div>
                <div className="text-sm text-gray-600">Communication</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-xl font-bold mb-1 ${getScoreColor(finalAssessment.cultureFitScore || 0)}`}>{finalAssessment.cultureFitScore || 0}%</div>
                <div className="text-sm text-gray-600">Culture Fit</div>
              </div>
            </div>
          </div>
        )}

        {/* Conversation History */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Conversation History
          </h3>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {session?.conversation?.map((item, index) => (
              <div key={index} className="border-l-4 border-gray-200 pl-4">
                {/* Question */}
                <div className="mb-2">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="font-medium text-blue-800 mb-1">Question {index + 1}</div>
                        <div className="text-gray-800">{item.question}</div>
                        <div className="text-xs text-gray-500 mt-2 flex items-center gap-4">
                          <span>Type: {item.questionType || 'general'}</span>
                          {item.targetArea && <span>Target: {item.targetArea}</span>}
                          <span>{new Date(item.timestamp).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Answer */}
                {item.response && (
                  <div className="mb-2 ml-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="font-medium text-green-800 mb-1">Candidate Response</div>
                          <div className="text-gray-800">{item.response}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* AI Analysis */}
                {item.aiAnalysis && (
                  <div className="ml-8 mb-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        AI Analysis
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.technicalScore || 0)}`}>{item.aiAnalysis.technicalScore || 0}</div>
                          <div className="text-xs text-gray-600">Technical</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.behavioralScore || 0)}`}>{item.aiAnalysis.behavioralScore || 0}</div>
                          <div className="text-xs text-gray-600">Behavioral</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.communicationScore || 0)}`}>{item.aiAnalysis.communicationScore || 0}</div>
                          <div className="text-xs text-gray-600">Communication</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${getScoreColor(item.aiAnalysis.cultureFitScore || 0)}`}>{item.aiAnalysis.cultureFitScore || 0}</div>
                          <div className="text-xs text-gray-600">Culture Fit</div>
                        </div>
                      </div>
                      {/* Red Flags */}
                      {item.aiAnalysis.redFlags && item.aiAnalysis.redFlags.length > 0 && (
                        <div className="mb-2">
                          <div className="font-medium text-red-700 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Red Flags:
                          </div>
                          <div className="space-y-1">
                            {item.aiAnalysis.redFlags.map((flag, flagIndex) => (
                              <div key={flagIndex} className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
                                • {flag}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Positive Indicators */}
                      {item.aiAnalysis.positiveIndicators && item.aiAnalysis.positiveIndicators.length > 0 && (
                        <div>
                          <div className="font-medium text-green-700 mb-1 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Positive Indicators:
                          </div>
                          <div className="space-y-1">
                            {item.aiAnalysis.positiveIndicators.map((indicator, indicatorIndex) => (
                              <div key={indicatorIndex} className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                                • {indicator}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final Assessment Details */}
        {finalAssessment && (
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">Final Assessment Details</h3>
            {/* Strengths */}
            {finalAssessment.strengths && finalAssessment.strengths.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium mb-2 text-green-700">Strengths:</h4>
                <div className="space-y-1">
                  {finalAssessment.strengths.map((strength, index) => (
                    <div key={index} className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                      • {strength}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Concerns */}
            {finalAssessment.concerns && finalAssessment.concerns.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium mb-2 text-red-700">Areas of Concern:</h4>
                <div className="space-y-1">
                  {finalAssessment.concerns.map((concern, index) => (
                    <div key={index} className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                      • {concern}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Detailed Analysis */}
            {finalAssessment.detailedAnalysis && (
              <div>
                <h4 className="font-medium mb-2">Detailed Analysis:</h4>
                <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 max-h-32 overflow-y-auto">
                  {finalAssessment.detailedAnalysis}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Accurate panel for the new agent-driven InterviewReport.
  const renderReport = () => {
    const r = report;
    const recoStyle = {
      strong_hire: 'bg-emerald-100 text-emerald-800', hire: 'bg-emerald-50 text-emerald-700',
      borderline: 'bg-amber-50 text-amber-700', no_hire: 'bg-rose-50 text-rose-700',
      strong_no_hire: 'bg-rose-100 text-rose-800',
    }[r.recommendation] || 'bg-slate-100 text-slate-700';
    const scoreColor = (s) => (s >= 75 ? 'text-emerald-600' : s >= 50 ? 'text-amber-600' : 'text-rose-600');

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 bg-slate-50 rounded-xl p-5">
          <div className="text-center">
            <div className={`text-3xl font-bold ${scoreColor(r.overallScore)}`}>{r.overallScore ?? '—'}<span className="text-base text-slate-400">/100</span></div>
            <div className="text-xs text-slate-500 mt-1">Overall Score</div>
          </div>
          <div className="text-center">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${recoStyle}`}>
              {(r.recommendation || 'pending').replace(/_/g, ' ')}
            </span>
            <div className="text-xs text-slate-500 mt-2">Recommendation</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-600">{r.transcript?.length || 0}</div>
            <div className="text-xs text-slate-500 mt-1">Conversation turns</div>
          </div>
        </div>

        {/* Scored on the stage's brief, and any protected topic the
            interviewer raised (@story HIR-HR21, 30 Oct 2026). */}
        <GuardrailFlags flags={r.guardrailFlags} />
        <SomethingWrong about="report" reportId={r._id} />
        <BriefRubricScores report={r} />

        {/* Axis scores (a stage without a brief) */}
        {!hasBriefScores(r) && r.axisScores?.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Assessment Scores</h3>
            <div className="space-y-2">
              {r.axisScores.map((a, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700">{a.axis}</span>
                    <span className={`font-bold ${scoreColor(a.score)}`}>{a.score}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded"><div className={`h-2 rounded ${a.score >= 75 ? 'bg-emerald-500' : a.score >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${a.score}%` }} /></div>
                  {a.note && <div className="text-xs text-slate-400 mt-0.5">{a.note}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strengths + flags */}
        <div className="grid grid-cols-2 gap-4">
          {r.strengths?.length > 0 && (
            <div className="bg-emerald-50 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 text-sm mb-2">Strengths</h4>
              <ul className="text-sm text-emerald-700 space-y-1 list-disc pl-4">{r.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {r.redFlags?.length > 0 && (
            <div className="bg-rose-50 rounded-lg p-4">
              <h4 className="font-semibold text-rose-800 text-sm mb-2">Concerns</h4>
              <ul className="text-sm text-rose-700 space-y-1 list-disc pl-4">{r.redFlags.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
        </div>

        {/* Narrative */}
        {r.selectionNarrative && (
          <div className="bg-indigo-50 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 text-sm mb-2">Selection narrative</h4>
            <p className="text-sm text-slate-700 leading-relaxed">{r.selectionNarrative}</p>
          </div>
        )}

        {/* Transcript */}
        {r.transcript?.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Full Transcript</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {r.transcript.map((t, i) => (
                <div key={i} className={`flex ${t.role === 'candidate' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${t.role === 'candidate' ? 'bg-blue-50 text-slate-800' : 'bg-slate-100 text-slate-800'}`}>
                    <div className="text-[10px] uppercase tracking-wide font-semibold mb-1 opacity-60">{t.role === 'candidate' ? 'Candidate' : 'Interviewer'}</div>
                    <div className="whitespace-pre-line">{t.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <span className="ml-3 text-gray-600">Loading interview details...</span>
        </div>
      );
    }

    // New agent-driven report takes precedence over the legacy panels.
    if (report) return renderReport();

    switch (interviewType) {
      case 'conversational':
      case 'preScreening':
        return renderConversationalInterview();
      case 'psychoanalytical':
        return renderPsychoanalyticalInterview();
      case 'finalInterview':
        return renderFinalInterview();
      default:
        return <div className="text-center text-gray-500 py-8">Unknown interview type</div>;
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
      title={
        <div className="flex items-center gap-3">
          <Brain className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-bold">{getInterviewTypeTitle()}</h2>
            <p className="text-sm text-gray-600">
              {resume?.candidateName || resume?.fileName || 'Candidate'} • {resume?.email}
            </p>
          </div>
        </div>
      }
    >
      <div className="max-h-[70vh] overflow-y-auto">
        {renderContent()}
      </div>
    </Modal>
  );
};

export default InterviewDetailsModal; 